import { Center, Flex, Stack } from '@chakra-ui/react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { Button } from '@main/complykit/components/button';
import { Callout } from '@main/complykit/components/callout';
import { Text } from '@main/complykit/components/text';

import { DnsRecord, DnsRecordsTable } from './dns-records-table';
import { DomainRemoveButton } from './domain-remove-button';
import { useCooldown } from './use-cooldown';

const VERIFY_RETRY_COOLDOWN_MS = 60_000;

export type DomainDnsRecordsLabels = {
  intro: string;
  failed: (invalidCount: number) => string;
  noRecords: string;
  exportCsv: string;
  verify: string;
  remove: string;
  retryCooldown: (secondsLeft: number) => string;
};

type DomainDnsRecordsProps = {
  records: DnsRecord[];
  isFailed: boolean;
  onVerify: () => Promise<boolean>;
  isVerifying: boolean;
  onRemove: () => void;
  isRemoving: boolean;
  onExportCsv: () => void;
  labels: DomainDnsRecordsLabels;
  canManageDomain?: boolean;
};

export const DomainDnsRecords = ({
  records,
  isFailed,
  onVerify,
  isVerifying,
  onRemove,
  isRemoving,
  onExportCsv,
  labels,
  canManageDomain,
}: DomainDnsRecordsProps) => {
  const verifyCooldown = useCooldown();
  const invalidRecords = records.filter((record) => !record.valid);
  const isVerifyDisabled = isVerifying || verifyCooldown.isActive;

  const handleVerify = async () => {
    const isAllValid = await onVerify();
    if (!isAllValid) {
      verifyCooldown.start(VERIFY_RETRY_COOLDOWN_MS);
    }
  };

  return (
    <Stack spacing={5}>
      <Text size="sm" color="muted">
        {labels.intro}
      </Text>
      {isFailed && <Callout status="error">{labels.failed(invalidRecords.length)}</Callout>}
      {records.length > 0 ? (
        <Stack spacing={3}>
          <Flex justifyContent="flex-end">
            <Button variant="outline" iconStart={ArrowDownTrayIcon} onPress={onExportCsv}>
              {labels.exportCsv}
            </Button>
          </Flex>
          <DnsRecordsTable records={records} />
        </Stack>
      ) : (
        <Center py={6}>
          <Text size="sm" color="muted">
            {labels.noRecords}
          </Text>
        </Center>
      )}
      {canManageDomain && (
        <Stack spacing={1} alignItems="flex-end">
          <Flex gap={3}>
            <DomainRemoveButton label={labels.remove} onClick={onRemove} isLoading={isRemoving} />
            <Button isPending={isVerifying} isDisabled={isVerifyDisabled} onPress={handleVerify}>
              {labels.verify}
            </Button>
          </Flex>
          {verifyCooldown.isActive && (
            <Text color="muted" size="sm" align="end">
              {labels.retryCooldown(verifyCooldown.secondsLeft)}
            </Text>
          )}
        </Stack>
      )}
    </Stack>
  );
};
